import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Link2, Mail, MessageCircle, Ban } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { care, fail } from './careApi';
import { Btn, Input } from './careUi';

const FORMS = [['admission', 'Resident Admission Form'], ['pdpa', 'PDPA Notice and Consent'], ['contract', "Resident's Contract"]];

export default function SignLinkDialog({ r, open, onClose }) {
  const [forms, setForms] = useState(FORMS.map((x) => x[0]));
  const [email, setEmail] = useState('');
  const [link, setLink] = useState(null);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState('');
  const loadLinks = () => care.get(`/residents/${r.id}/sign-links`).then((x) => setLinks(x.data)).catch(() => {});
  useEffect(() => { if (open) { setLink(null); setEmail(r.representative_email || ''); loadLinks(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (sendEmail) => {
    setBusy(sendEmail ? 'email' : 'create');
    try {
      const { data } = await care.post(`/residents/${r.id}/sign-links`, { forms, email: sendEmail ? email : null, origin_url: window.location.origin });
      setLink(data); loadLinks();
      if (sendEmail && !data.emailed) toast.warning('Link created, but the email could not be sent. Copy the link below and share it instead.');
      else toast.success(sendEmail ? `Signing link emailed to ${email}` : 'Signing link created');
    } catch (e) { fail(e); } finally { setBusy(''); }
  };
  const revoke = async (t) => { try { await care.post(`/sign-links/${t}/revoke`); toast.success('Link revoked'); loadLinks(); } catch (e) { fail(e); } };
  const wa = link && `https://wa.me/?text=${encodeURIComponent(`Please review and sign the documents for ${r.name}: ${link.url}`)}`;
  const now = new Date().toISOString();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sign-link-dialog">
        <DialogHeader><DialogTitle>Family signing link</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Send the family a secure link to read and sign on their phone. Valid for 14 days.</p>
        <div className="space-y-2">
          {FORMS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="w-4 h-4 accent-teal-700" checked={forms.includes(k)} data-testid={`sign-link-form-${k}`}
                onChange={() => setForms(forms.includes(k) ? forms.filter((x) => x !== k) : [...forms, k])} />{l}
            </label>
          ))}
        </div>
        <Input label="Family email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testid="sign-link-email" />
        <div className="flex flex-wrap gap-2">
          <Btn loading={busy === 'create'} disabled={!forms.length} onClick={() => create(false)} data-testid="sign-link-create"><Link2 className="w-4 h-4" />Create link</Btn>
          <Btn variant="ghost" loading={busy === 'email'} disabled={!forms.length || !email} onClick={() => create(true)} data-testid="sign-link-send-email"><Mail className="w-4 h-4" />Create & email</Btn>
        </div>
        {link && (
          <div className="rounded-xl bg-slate-50 p-3 space-y-2" data-testid="sign-link-result">
            <p className="text-xs break-all text-slate-700" data-testid="sign-link-url">{link.url}</p>
            <div className="flex gap-2">
              <Btn variant="ghost" className="!py-1.5" onClick={() => { navigator.clipboard.writeText(link.url); toast.success('Link copied'); }} data-testid="sign-link-copy"><Copy className="w-4 h-4" />Copy</Btn>
              <a href={wa} target="_blank" rel="noreferrer" data-testid="sign-link-whatsapp" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold bg-white border border-slate-300 hover:bg-slate-50 text-slate-700"><MessageCircle className="w-4 h-4" />WhatsApp</a>
            </div>
          </div>
        )}
        {links.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Previous links</p>
            {links.map((l) => {
              const live = !l.revoked && l.expires_at > now;
              return (
                <div key={l.token} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-100">
                  <span>{l.created_at.slice(0, 10)} · {l.forms.length} form(s) · {live ? `valid to ${l.expires_at.slice(0, 10)}` : l.revoked ? 'revoked' : 'expired'}</span>
                  {live && <button onClick={() => revoke(l.token)} className="text-rose-600 inline-flex items-center gap-1" data-testid={`sign-link-revoke-${l.token.slice(0, 6)}`}><Ban className="w-3 h-3" />Revoke</button>}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
